// macadamia-app/src/macadamia-core/components/widgets/RichTextListItem.tsx

import React, { useRef, useEffect } from 'react';
import SubBlockEditor from './composite-text-widget/SubBlockEditor';
import { ITextBlock, ISuggestion } from './types';
import { generateListItemBlockId } from './utils/blockIdUtils';

/**
 * Props pour RichTextListItem
 */
interface RichTextListItemProps {
  // Contenu HTML de l'item
  content: string;

  // Callback appelé quand le contenu change
  onChange: (newContent: string) => void;

  // Mode d'affichage
  mode: 'editor' | 'reader';

  // ID unique de l'item (utilisé pour le tracking des références)
  itemId: string;

  // ID du bloc parent (liste)
  parentBlockId: string;

  // Infos du chapitre
  chapterIndex: number;
  chapterTitle: string;

  // ID du dossier
  dossierId: string;

  // Callback pour les suggestions (mode reader)
  onSuggestionCreated?: (suggestion: ISuggestion) => void;

  // Role de l'utilisateur
  userRole: string | null;

  // Ref optionnelle pour accéder au div éditable
  editorRef?: React.RefObject<HTMLDivElement>;

  // Callbacks pour la navigation clavier (mode editor seulement)
  onTabPress?: () => void;
  onShiftTabPress?: () => void;
  onEnterPress?: () => void;
  onBackspaceAtStart?: () => void;

  // Indique si cet item doit prendre le focus automatiquement
  shouldFocus?: boolean;
}

/**
 * RichTextListItem - Wrapper autour de SubBlockEditor pour les items de liste
 *
 * Ce composant adapte SubBlockEditor (conçu pour les blocs de texte composite)
 * pour fonctionner avec les items de liste. Il transforme le contenu HTML simple
 * en ITextBlock et génère un ID composite pour le tracking des références.
 */
const RichTextListItem: React.FC<RichTextListItemProps> = ({
  content,
  onChange,
  mode,
  itemId,
  parentBlockId,
  chapterIndex,
  chapterTitle,
  dossierId,
  onSuggestionCreated,
  userRole,
  editorRef,
  onTabPress,
  onShiftTabPress,
  onEnterPress,
  onBackspaceAtStart,
  shouldFocus = false
}) => {
  // Ref locale pour trouver l'élément éditable
  const containerRef = useRef<HTMLDivElement>(null);

  // Générer un ID composite unique pour cet item en utilisant la fonction helper
  // Format: {parentBlockId}-item-{itemId}
  const compositeBlockId = generateListItemBlockId(parentBlockId, itemId);

  // Effet pour gérer le focus automatique quand shouldFocus devient true
  useEffect(() => {
    if (shouldFocus && mode === 'editor') {
      // On cherche l'élément contentEditable dans ce composant
      const root = editorRef?.current || containerRef.current;

      if (root) {
        const editableElement = root.querySelector('[contenteditable="true"]') as HTMLElement;
        if (editableElement) {
          editableElement.focus();
        }
      }
    }
  }, [shouldFocus, mode, editorRef]);

  // Transformer le contenu HTML simple en ITextBlock
  // SubBlockEditor attend un ITextBlock avec { id, _id, rawText, entities }
  const textBlock: ITextBlock = {
    id: itemId,
    _id: itemId,
    rawText: content,
    entities: [] // Les entités sont maintenant gérées via le HTML inline
  };

  // Handler quand le bloc change
  const handleTextBlockChange = (newTextBlock: ITextBlock) => {
    // Extraire le HTML et le passer au parent
    onChange(newTextBlock.rawText);
  };

  // Handler par défaut pour les suggestions si non fourni
  const handleSuggestionCreated = onSuggestionCreated || ((suggestion: ISuggestion) => {
  });

  // Handler pour les événements clavier (navigation liste)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Seulement en mode editor
    if (mode !== 'editor') return;

    // Tab : Indenter (toujours intercepter Tab pour la navigation)
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      onTabPress?.();
      return;
    }

    // Shift+Tab : Désindenter
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      onShiftTabPress?.();
      return;
    }

    // Enter : Ne créer un nouvel item QUE si on a un handler ET qu'on n'est pas en train d'éditer
    // Laisser passer l'Enter normal pour permettre l'édition dans le contentEditable
    // On ne l'intercepte que pour créer un nouvel item de liste
    if (e.key === 'Enter' && !e.shiftKey && onEnterPress) {
      // Vérifier si le contentEditable a le focus
      const editableDiv = e.currentTarget.querySelector('[contenteditable="true"]');
      if (editableDiv && document.activeElement === editableDiv) {
        e.preventDefault();
        onEnterPress();
        return;
      }
    }

    // Backspace : Gérer suppression/désindentation
    if (e.key === 'Backspace' && onBackspaceAtStart) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        if (range.collapsed && range.startOffset === 0) {
          const editableDiv = e.currentTarget.querySelector('[contenteditable="true"]');
          if (editableDiv) {
            // Utiliser TreeWalker pour trouver le premier noeud texte
            const treeWalker = document.createTreeWalker(
              editableDiv,
              NodeFilter.SHOW_TEXT,
              null
            );
            const firstTextNode = treeWalker.firstChild();

            // Déclencher si on est au début du premier noeud texte ou du contentEditable
            if (!firstTextNode || range.startContainer === firstTextNode ||
                range.startContainer === editableDiv) {
              e.preventDefault();
              onBackspaceAtStart();
              return;
            }
          }
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="rich-text-list-item"
      onKeyDown={handleKeyDown}
    >
      <SubBlockEditor
        textBlock={textBlock}
        onTextBlockChange={handleTextBlockChange}
        mode={mode}
        dossierId={dossierId}
        onSuggestionCreated={handleSuggestionCreated}
        compositeBlockId={compositeBlockId}
        userRole={userRole}
        chapterIndex={chapterIndex}
        chapterTitle={chapterTitle}
      />
    </div>
  );
};

export default RichTextListItem;
